import { differenceInDays, isValid, startOfDay, parseISO, format, addDays, getDay, isWeekend as isDateFnsWeekend } from 'date-fns';
import { nl } from 'date-fns/locale/nl';
import type { AgendaOverviewDay } from '@debatdirect/common/types/debatdirect-typings';

import { injectable } from '../modules/container';

@injectable()
export default class DateService {
  /**
   * Check if given date string is a valid path date (yyyy-mm-dd).
   */
  isValidPathDate(dateString: string) {
    if (typeof dateString !== 'string' || !/\d{4}-\d{2}-\d{2}/.test(dateString)) {
      return false;
    }

    const date = parseISO(dateString);

    return isValid(date);
  }

  protected weekDatesCache: Record<string, string[]> = {};

  /**
   * Create week dates for given date.
   */
  createWeekDates(dateString: string) {
    if (Object.prototype.hasOwnProperty.call(this.weekDatesCache, dateString)) {
      return this.weekDatesCache[dateString];
    }

    let date = parseISO(dateString);
    let day = getDay(date) || 7;

    while (day > 1) {
      date = addDays(date, -1);
      day = getDay(date);
    }

    const result = [];

    while (result.length < 7) {
      result.push(format(date, 'yyyy-MM-dd'));
      date = addDays(date, 1);
    }

    this.weekDatesCache[dateString] = result;

    return result;
  }

  /**
   * Returns true if the given date is in the weekend (saturday or sunday)
   */
  isWeekend(date: string) {
    const parsedDate = parseISO(date);
    return isDateFnsWeekend(parsedDate);
  }

  /**
   * Check if we have debates in the weekend.
   */
  hasWeekendDebates(agendaDate: string, currentDate: string, agendaDays: Record<string, AgendaOverviewDay> | undefined) {
    if (!this.isValidPathDate(agendaDate)) {
      return false;
    }

    // if the requested date or the current day is in the weekend
    if (this.isWeekend(currentDate) || this.isWeekend(agendaDate)) {
      return true;
    }

    const days = agendaDays || {};
    const dayValues = Object.values(days);
    const upcomingDays = dayValues.slice(dayValues.findIndex(({ isCurrentDate }) => isCurrentDate));

    return upcomingDays.some((overviewDay) => ['Sat', 'Sun'].includes(overviewDay.day) && overviewDay.debateCount > 0);
  }

  /**
   * Get difference, measured in days, between agenda date and current date.
   */
  getAgendaDateDifference(forAgendaDate?: string) {
    if (!forAgendaDate) {
      return 0;
    }

    const agendaDate = parseISO(forAgendaDate);

    if (!isValid(agendaDate)) {
      return 0;
    }

    return differenceInDays(startOfDay(agendaDate), startOfDay(new Date()));
  }

  /**
   * Check if agenda date is today.
   */
  isToday() {
    return this.getAgendaDateDifference() === 0;
  }

  /**
   * Check if agenda date is in the future.
   */
  isFutureDate() {
    return this.getAgendaDateDifference() > 0;
  }

  /**
   * Get a user-friendly agenda date.
   */
  getUserFriendlyAgendaDate(dateString: string) {
    const dayDiff = this.getAgendaDateDifference(dateString);

    if (isNaN(dayDiff)) {
      return '';
    }

    switch (dayDiff) {
      case 0:
        return 'vandaag';
      case 1:
        return 'morgen';
      case -1:
        return 'gisteren';
      default:
    }

    const date = parseISO(dateString);

    if (!isValid(date)) {
      return '';
    }

    return format(date, 'EEEE d MMMM yyyy', { locale: nl });
  }
}
